import React, {useRef, useState} from 'react';
import {Box, Stack, Typography} from '@mui/material';
import {t} from 'i18next';
import {v4 as uuidv4} from 'uuid';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ArrOperatorButton from '../operator-button/OperatorButton';
import FieldTypeDrawer from '../field-type-drawer/FieldTypeDrawer';
import MetadataFieldType from '../field-types/metadata-field-type/MetadataFieldType';
import CommentsFieldType from '../field-types/comments-field-type/CommentsFieldType';
import ChipPanel, {ChipPanelHandle} from '../query-panel/chip-panel/ChipPanel';
import {Query} from '../../../types/Query';
import {Criteria, CriteriaType} from '../../../types/Criteria';
import {Field} from '../../../types/Field';
import {getOperatorLabel} from '../operatorMenuUtils';

class TemporaryCriteria implements Criteria {
  id = uuidv4();
  type: CriteriaType = 'Operand';
}

export interface ContentAndMetadataProps {
  query: Query;
  indexID?: number;
  onCriteriaChanged?: (query: Query) => void;
}

const ContentAndMetadata: React.FC<ContentAndMetadataProps> = ({
  query,
  indexID,
  onCriteriaChanged,
}) => {
  const [isMetadataDrawerOpen, setIsMetadataDrawerOpen] =
    useState<boolean>(false);
  const [canSubmitMetadata, setCanSubmitMetadata] = useState<boolean>(false);
  const [isCommentsDrawerOpen, setIsCommentsDrawerOpen] =
    useState<boolean>(false);
  const [canSubmitComment, setCanSubmitComment] = useState<boolean>(false);
  let tempCriteria: Criteria = new TemporaryCriteria();
  const chipPanelRef = useRef<ChipPanelHandle>(null);

  const handleChipCriteriaChanged = (updatedCriteria: Criteria[]) => {
    const updatedQuery = {
      ...query,
      value: [...updatedCriteria],
    };
    onCriteriaChanged && onCriteriaChanged(updatedQuery);
  };

  const handleDrawerCriteriaChange = (
    field: Field,
    operator: string | null,
    value: string[]
  ) => {
    if (field.isRange) {
      if (operator === 'range') {
        // Range provided
        tempCriteria.value = `${field.text}: [${value[0]} TO ${value[1]}]`;
      } else {
        // Range is possible but not provided
        tempCriteria.value = `${field.text}: [${value[0]} TO ${value[0]}]`;
      }
    } else {
      // Single value
      tempCriteria.value = `${field.text}: (${value[0]})`;
    }
  };

  const openMetadataDrawer = () => {
    tempCriteria = new TemporaryCriteria();
    setIsMetadataDrawerOpen(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleMetadataClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    openMetadataDrawer();
  };

  const handleMetadataKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      e.stopPropagation();
      openMetadataDrawer();
    }
  };

  const openCommentsDrawer = () => {
    tempCriteria = new TemporaryCriteria();
    setIsCommentsDrawerOpen(true);
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCommentsClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    openCommentsDrawer();
  };

  const handleCommentsKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      e.stopPropagation();
      openCommentsDrawer();
    }
  };

  const handleDrawerClose = () => {
    setIsMetadataDrawerOpen(false);
    setCanSubmitMetadata(false);
    setIsCommentsDrawerOpen(false);
    setCanSubmitComment(false);
  };

  const handleDrawerSave = () => {
    chipPanelRef.current?.insertCriteria({...tempCriteria});

    setIsMetadataDrawerOpen(false);
    setCanSubmitMetadata(false);
    setIsCommentsDrawerOpen(false);
    setCanSubmitComment(false);
  };

  const handleOperatorSelected = (id: string) => {
    const newCriteria: Criteria = {
      id: uuidv4(),
      type: 'Operator',
      value: getOperatorLabel(id),
    };

    chipPanelRef.current?.insertCriteria(newCriteria);
  };

  return (
    <>
      {/* Insert: [Metadata] [Comments] [Operators] */}
      <Stack spacing={1} className="content_and_metadata-content-panel">
        <Stack direction={'row'} className="query-actions">
          <Typography
            className="typo-text label3_size query-actions-label"
            data-testid={`content-and-metadata-insert`}
          >
            {t('sessionSearch.queryList.contentAndMetadata.insert')}
          </Typography>
          <ArrButton
            size="small"
            variant="secondary"
            onClick={handleMetadataClick}
            onKeyDown={handleMetadataKeyDown}
            className="button-cam-add-metadata"
            dataTestId="button-cam-add-metadata"
          >
            {t('sessionSearch.queryList.contentAndMetadata.metadata')}
          </ArrButton>
          <ArrButton
            size="small"
            variant="secondary"
            onClick={handleCommentsClick}
            onKeyDown={handleCommentsKeyDown}
            className="button-cam-add-comments"
            dataTestId="button-cam-add-comments"
          >
            {t('sessionSearch.queryList.contentAndMetadata.comments')}
          </ArrButton>
          <ArrOperatorButton
            onOperatorSelected={handleOperatorSelected}
            dataTestId="cmt"
            restoreFocusOnSelect={false}
          />
        </Stack>
        <Box
          className="query-chips"
          data-testid="chip-panel-cmt-criteria-wrapper"
        >
          <ChipPanel
            ref={chipPanelRef}
            criteria={query.value}
            onCriteriaChange={handleChipCriteriaChanged}
            dataTestId="cmt-panel"
          />
        </Box>
      </Stack>
      {/* Drawers */}
      {isMetadataDrawerOpen && (
        <FieldTypeDrawer
          title={t('sessionSearch.queryList.contentAndMetadata.metadata')}
          isOpen={isMetadataDrawerOpen}
          onClose={handleDrawerClose}
          onSave={handleDrawerSave}
          canSave={canSubmitMetadata}
          disableRestoreFocus
        >
          <MetadataFieldType
            type="METADATALIST"
            indexID={indexID}
            onCriteriaChaged={handleDrawerCriteriaChange}
            onValidated={(isValid) => setCanSubmitMetadata(isValid)}
          />
        </FieldTypeDrawer>
      )}
      {isCommentsDrawerOpen && (
        <FieldTypeDrawer
          title={t('sessionSearch.queryList.contentAndMetadata.comments')}
          isOpen={isCommentsDrawerOpen}
          onClose={handleDrawerClose}
          onSave={handleDrawerSave}
          canSave={canSubmitComment}
          disableRestoreFocus
        >
          <CommentsFieldType
            type="COMMENTS"
            onCriteriaChaged={handleDrawerCriteriaChange}
            onValidated={(isValid) => setCanSubmitComment(isValid)}
          />
        </FieldTypeDrawer>
      )}
    </>
  );
};

export default ContentAndMetadata;
